import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Download, Info, Table2, XCircle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Cabecera } from "@/components/propuesta/cabecera";
import { Aviso, Caja, Tile } from "@/components/propuesta/piezas";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/cierre")({ component: Cierre });

type FilaEmpresa = {
  empresa: string;
  conciliado: number;
  porDecidir: number;
  prestamo: number;
  sinRespaldo: number;
  total: number;
};
type Regla = { regla: string; que: string; cuantos: number };
type Requisito = { cumple: boolean; titulo: string; detalle: string };

// ===========================================================================
// El cierre del periodo (lámina 5).
//
// Tabla dinámica de Odoo: empresas en las filas, estados en las columnas, y el
// total del grupo abajo. A la derecha, qué sale del sistema y qué falta para
// poder cerrar.
//
// UNA MONEDA POR TABLA. Sumar soles y dólares en la misma columna daría un
// «total del grupo» que no existe mientras no haya tipo de cambio cargado — y
// este es el reporte que va a contabilidad.
// ===========================================================================

function Cierre() {
  const [moneda, setMoneda] = useState<"PEN" | "USD">("PEN");
  const { data, isPending } = api.cierre.useQuery(["cierre", moneda], {
    query: { moneda },
  });
  const d = data?.status === 200 ? data.body : null;

  const dinero = (c: number) =>
    `${(c / 100).toLocaleString("es-PE", { maximumFractionDigits: 0 })}`;

  const qc = useQueryClient();
  const cerrar = api.cerrarPeriodo.useMutation();

  // El servidor vuelve a comprobar los requisitos, así que lo que llega aquí
  // cuando dice que no es el motivo de verdad: se enseña tal cual en vez de un
  // «no se pudo» que obligaría a ir a buscarlo.
  const alCerrar = {
    onSuccess: (r: {
      status: number;
      body: { dice?: string; message?: string };
    }) => {
      if (r.status === 200) {
        toast.success(r.body.dice ?? "Periodo cerrado");
        qc.invalidateQueries();
      } else {
        toast.error(r.body.message ?? "No se pudo cerrar el periodo");
      }
    },
    onError: () => toast.error("No se pudo cerrar el periodo"),
  };

  const requisitos = (d?.requisitos ?? []) as Requisito[];
  const cumplidos = requisitos.filter((r) => r.cumple).length;

  return (
    <>
      <Cabecera
        migas={["Reportes"]}
        titulo="Cierre del periodo"
        // Sin `marcador`: esta pantalla no filtra, y la caja deshabilitada
        // que salía aquí decía lo contrario.
        facetas={[
          { clave: "Periodo", valor: d?.periodo ?? "—" },
          {
            clave: "Medida",
            valor: moneda === "PEN" ? "Importe en soles" : "Importe en dólares",
            tono: "gris" as const,
            alQuitar: () => setMoneda(moneda === "PEN" ? "USD" : "PEN"),
          },
        ]}
        acciones={
          <>
            <Button size="sm">
              <Download className="mr-2 size-4" />
              Generar el Excel del mes
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setMoneda(moneda === "PEN" ? "USD" : "PEN")}
            >
              <Table2 className="mr-2 size-4" />
              Ver en {moneda === "PEN" ? "dólares" : "soles"}
            </Button>
          </>
        }
      />

      {isPending || !d ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className="grid gap-4 xl:grid-cols-[1.7fr_1fr]">
          <div className="space-y-4">
            <Caja
              titulo={"Importe por empresa y estado"}
              derecha={moneda === "PEN" ? "en soles" : "en dólares"}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                  <thead className="border-b text-[11px] text-muted-foreground uppercase tracking-wide">
                    <tr>
                      <th className="py-2 text-left font-medium">Empresa</th>
                      <th className="py-2 text-right font-medium">
                        Conciliado
                      </th>
                      <th className="py-2 text-right font-medium">
                        Por decidir
                      </th>
                      <th className="py-2 text-right font-medium">Préstamo</th>
                      <th className="py-2 text-right font-medium">
                        Sin respaldo
                      </th>
                      <th className="py-2 text-right font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(d.filas as FilaEmpresa[]).map((f) => (
                      <tr key={f.empresa} className="border-b">
                        <td className="py-2.5 font-medium">{f.empresa}</td>
                        <td className="bg-emerald-500/5 py-2.5 text-right tabular-nums">
                          {f.conciliado ? dinero(f.conciliado) : "—"}
                        </td>
                        <td className="py-2.5 text-right tabular-nums">
                          {f.porDecidir ? dinero(f.porDecidir) : "—"}
                        </td>
                        <td className="py-2.5 text-right tabular-nums">
                          {f.prestamo ? dinero(f.prestamo) : "—"}
                        </td>
                        <td className="py-2.5 text-right tabular-nums">
                          {f.sinRespaldo ? dinero(f.sinRespaldo) : "—"}
                        </td>
                        <td className="py-2.5 text-right font-semibold tabular-nums">
                          {dinero(f.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">
                      <td className="py-2.5 pl-2 font-semibold">
                        Total del grupo
                      </td>
                      <td className="py-2.5 text-right font-semibold tabular-nums">
                        {dinero(d.totales.conciliado)}
                      </td>
                      <td className="py-2.5 text-right font-semibold tabular-nums">
                        {dinero(d.totales.porDecidir)}
                      </td>
                      <td className="py-2.5 text-right font-semibold tabular-nums">
                        {dinero(d.totales.prestamo)}
                      </td>
                      <td className="py-2.5 text-right font-semibold tabular-nums">
                        {dinero(d.totales.sinRespaldo)}
                      </td>
                      <td className="py-2.5 pr-2 text-right font-semibold tabular-nums">
                        {dinero(d.totales.total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Caja>

            <div className="grid gap-3.5 sm:grid-cols-3">
              <Tile
                titulo="Cerrado sin intervención"
                valor={d.cerradoSinIntervencion.toLocaleString("es-PE")}
                unidad="%"
                tono={d.cerradoSinIntervencion > 50 ? "ok" : "warn"}
                pie={`de ${d.movimientos.toLocaleString("es-PE")} movimientos`}
              />
              <Tile
                titulo="Por decidir"
                valor={dinero(d.totales.porDecidir)}
                tono="warn"
                pie="esperando a una persona"
              />
              <Tile
                titulo="Sin respaldo"
                valor={dinero(d.totales.sinRespaldo)}
                tono={d.totales.sinRespaldo > 0 ? "bad" : "neutro"}
                pie="dinero que entró sin voucher"
              />
            </div>

            <Caja
              titulo="Cuánto resolvió cada regla"
              derecha={`${d.movimientos.toLocaleString("es-PE")} movimientos`}
            >
              <div className="space-y-3">
                {(d.reglas as Regla[]).map((r) => {
                  const mayor = Math.max(
                    ...(d.reglas as Regla[]).map((x) => x.cuantos),
                    1,
                  );
                  return (
                    <div key={r.regla} className="flex items-center gap-3">
                      <span className="w-10 shrink-0 rounded border px-1.5 py-0.5 text-center font-mono text-[11px]">
                        {r.regla}
                      </span>
                      <span className="flex-1 text-[12.5px]">{r.que}</span>
                      <span className="shrink-0 font-semibold text-[13px] tabular-nums">
                        {r.cuantos.toLocaleString("es-PE")}
                      </span>
                      <div className="h-2 w-40 shrink-0 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${(r.cuantos / mayor) * 100}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
                <p className="pt-1 text-[11.5px] text-muted-foreground">
                  Las que esperan a una persona <b>no son fallos del sistema</b>
                  : son los casos en que la evidencia no daba para cerrar solo.
                </p>
              </div>
            </Caja>
          </div>

          <div className="space-y-4">
            <Caja
              titulo={`Antes de cerrar ${d.periodo}`}
              derecha={`${cumplidos} de ${requisitos.length}`}
            >
              <div className="space-y-3.5">
                {requisitos.map((r) => (
                  <div key={r.titulo} className="flex items-start gap-2.5">
                    {r.cumple ? (
                      <CheckCircle2 className="mt-px size-4 shrink-0 text-emerald-600" />
                    ) : (
                      <XCircle className="mt-px size-4 shrink-0 text-red-600" />
                    )}
                    <div>
                      <div className="font-medium text-[12.5px]">
                        {r.titulo}
                      </div>
                      <div className="text-[11.5px] text-muted-foreground">
                        {r.detalle}
                      </div>
                    </div>
                  </div>
                ))}
                <Aviso tono="info">
                  <Info className="mt-px size-4 shrink-0" />
                  Un periodo cerrado se puede volver a abrir, pero queda
                  registrado quién lo hizo y cuándo.
                </Aviso>
              </div>
            </Caja>

            <Caja
              titulo="Qué sale del sistema"
              derecha="un archivo por empresa"
            >
              <div className="space-y-3">
                <div>
                  <div className="font-medium text-[12.5px]">
                    Hoja 1 · Estado de cuenta conciliado
                  </div>
                  <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                    Cada movimiento del banco con su comprobante al lado, la
                    regla que lo decidió y quién lo confirmó.
                  </p>
                </div>
                <div>
                  <div className="font-medium text-[12.5px]">
                    Hoja 2 · Importación contable
                  </div>
                  <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                    No se puede escribir sin ver un archivo real: cada
                    instalación tiene su plan de cuentas y sus centros de costo.
                  </p>
                </div>
              </div>
            </Caja>
          </div>
        </div>
      )}

      {d ? (
        <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur md:-mx-6 md:px-6 lg:-mx-8 lg:px-8">
          <p className="text-[12.5px]">
            {d.periodo} · <b>{dinero(d.totales.total)}</b>{" "}
            {d.moneda === "PEN" ? "soles" : "dólares"} en{" "}
            {d.movimientos.toLocaleString("es-PE")} movimientos
            <span
              className={cn(
                "ml-1",
                cumplidos < requisitos.length && "text-amber-600",
              )}
            >
              · faltan {requisitos.length - cumplidos} requisitos
            </span>
          </p>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a href="/exportar/cierre.csv" download>
                Descargar el cierre
              </a>
            </Button>
            {/* EL PRODUCTO FINAL, que hasta ahora no tenía botón.
                El Excel de CONCAR estaba construido, probado celda por celda
                contra la plantilla y servido en `/exportar/asientos.xlsx` — y
                no se enlazaba desde ninguna pantalla: la única forma de
                conseguirlo era escribir la URL a mano. Un producto final al
                que no se llega es un producto final que no existe. */}
            <Button variant="outline" size="sm" asChild>
              <a href="/exportar/asientos.xlsx" download>
                Excel para CONCAR
              </a>
            </Button>
            <Button
              size="sm"
              disabled={cumplidos < requisitos.length || cerrar.isPending}
              onClick={() =>
                cerrar.mutate({ body: { confirmo: true } }, alCerrar)
              }
            >
              Cerrar el periodo
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
